import { z } from "zod";
import {
    AccrualExtractionLineItemSchema,
    accrualMonetaryAmountSchema,
    accrualNonnegativeMonetaryAmountSchema,
    accrualQuantitySchema,
    accrualUnitPriceSchema,
} from "@common/dto/financialDocument/accrual-document-extraction.schema";

export const AccrualExtractionIncrementalRowUpsertSchema =
    AccrualExtractionLineItemSchema.extend({
        rowIndex: z.number().int().min(0).max(499),
        item: z.string().max(500).nullable().default(null),
        quantity: accrualQuantitySchema.default(null),
        unitPrice: accrualUnitPriceSchema.default(null),
        acceptAccountSuggestion: z.literal(true).optional(),
    });

export const InvoiceExtractionIncrementalRowUpsertSchema =
    AccrualExtractionIncrementalRowUpsertSchema.extend({
        catalogItemId: z.string().uuid().nullable().optional(),
        catalogItemPriceId: z.string().uuid().nullable().optional(),
    });

export const BillExtractionIncrementalRowUpsertSchema =
    AccrualExtractionIncrementalRowUpsertSchema;

interface BatchUpdateExtractionRowsBody {
    upserts: Array<{ rowIndex: number }>;
    deletes: number[];
}

function refineUniqueUpsertRowIndexes(
    body: BatchUpdateExtractionRowsBody,
    context: z.RefinementCtx,
): void {
    const rowIndexes = body.upserts.map((upsert) => upsert.rowIndex);
    if (new Set(rowIndexes).size !== rowIndexes.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Duplicate rowIndex in upserts",
            path: ["upserts"],
        });
    }
}

export const BatchUpdateBillExtractionRowsBodySchema = z
    .object({
        upserts: z.array(BillExtractionIncrementalRowUpsertSchema).max(500),
        deletes: z.array(z.number().int().min(0).max(499)).max(500),
    })
    .superRefine(refineUniqueUpsertRowIndexes);

export const BatchUpdateInvoiceExtractionRowsBodySchema = z
    .object({
        upserts: z.array(InvoiceExtractionIncrementalRowUpsertSchema).max(500),
        deletes: z.array(z.number().int().min(0).max(499)).max(500),
    })
    .superRefine(refineUniqueUpsertRowIndexes);

export const UpdateAccrualExtractionDetailsBodySchema = z.object({
    counterpartyName: z.string().max(500).nullable().optional(),
    counterpartyId: z.string().uuid().nullable().optional(),
    documentNumber: z.string().max(500).nullable().optional(),
    documentDate: z.string().date().nullable().optional(),
    dueDate: z.string().date().nullable().optional(),
    totalAmount: accrualMonetaryAmountSchema.nullable().optional(),
    taxAmount: accrualNonnegativeMonetaryAmountSchema.nullable().optional(),
    depositAmount: accrualNonnegativeMonetaryAmountSchema.nullable().optional(),
    memo: z.string().max(5000).nullable().optional(),
    balanceDue: accrualMonetaryAmountSchema.nullable().optional(),
    paymentDate: z.string().date().nullable().optional(),
    entityId: z.number().int().nullable().optional(),
    warnings: z.array(z.string().max(500)).max(50).nullable().optional(),
    acceptSuggestion: z.literal(true).optional(),
});

export const UpdateBillExtractionDetailsBodySchema =
    UpdateAccrualExtractionDetailsBodySchema;
